import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';
import { DEFAULT_REPORT_LANG, REPORT_LANG_OPTIONS } from '@/pages/reports/new/constants';

interface BasicInfoCardProps {
  title: string;
  titleError: string;
  onTitleChange: (_value: string) => void;
  edition: string;
  editionError?: string;
  onEditionChange: (_value: string) => void;
  partnerId: string;
  partnerError?: string;
  onPartnerChange: (_value: string) => void;
  partnerOptions: Array<{ value: string; label: string }>;
  lang: string;
  langError?: string;
  onLangChange: (_value: string) => void;
  showActions?: boolean;
  isSaving?: boolean;
  onCancel?: () => void;
  onSaveContinue?: () => void;
}

export const BasicInfoCard: React.FC<BasicInfoCardProps> = ({
  title,
  titleError,
  onTitleChange,
  edition,
  editionError = '',
  onEditionChange,
  partnerId,
  partnerError = '',
  onPartnerChange,
  partnerOptions,
  lang,
  langError = '',
  onLangChange,
  showActions = false,
  isSaving = false,
  onCancel,
  onSaveContinue,
}) => {
  const fields = (
    <div className="space-y-5">
      <div>
        <label className="mb-2 block text-sm font-medium text-gray-700">
          Título <span className="text-red-500">*</span>
        </label>
        <Input
          value={title}
          onChange={(e) => onTitleChange(e.target.value)}
          placeholder="Título do report"
          className={titleError ? 'border-red-500 focus:border-red-500' : ''}
        />
        {titleError && <p className="mt-1 text-sm text-red-500">{titleError}</p>}
      </div>
      <div>
        <label className="mb-2 block text-sm font-medium text-gray-700">
          Número da edição <span className="text-red-500">*</span>
        </label>
        <Input
          value={edition}
          onChange={(e) => onEditionChange(e.target.value)}
          placeholder="Ex: 128"
          className={editionError ? 'border-red-500 focus:border-red-500' : ''}
        />
        {editionError && <p className="mt-1 text-sm text-red-500">{editionError}</p>}
      </div>
      <div>
        <label className="mb-2 block text-sm font-medium text-gray-700">
          Marca <span className="text-red-500">*</span>
        </label>
        <Select value={partnerId || undefined} onValueChange={onPartnerChange}>
          <SelectTrigger
            className={cn(partnerError && 'border-red-500 focus:border-red-500')}
          >
            <SelectValue placeholder="Selecione" />
          </SelectTrigger>
          <SelectContent>
            {partnerOptions.map((opt) => (
              <SelectItem key={opt.value} value={opt.value}>
                {opt.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {partnerError && <p className="mt-1 text-sm text-red-500">{partnerError}</p>}
      </div>
      <div>
        <label className="mb-2 block text-sm font-medium text-gray-700">
          Idioma <span className="text-red-500">*</span>
        </label>
        <Select value={lang || DEFAULT_REPORT_LANG} onValueChange={onLangChange}>
          <SelectTrigger
            className={cn(langError && 'border-red-500 focus:border-red-500')}
          >
            <SelectValue placeholder="Selecione" />
          </SelectTrigger>
          <SelectContent>
            {REPORT_LANG_OPTIONS.map((opt) => (
              <SelectItem key={opt.value} value={opt.value}>
                {opt.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {langError && <p className="mt-1 text-sm text-red-500">{langError}</p>}
      </div>
      {showActions && (
        <div className="flex items-center justify-between gap-4 pt-2">
          <Button
            type="button"
            variant="outline"
            onClick={onCancel}
            disabled={isSaving}
          >
            Cancelar
          </Button>
          <Button
            type="button"
            variant="dark"
            onClick={onSaveContinue}
            disabled={isSaving}
          >
            {isSaving ? 'Salvando...' : 'Salvar e continuar'}
          </Button>
        </div>
      )}
    </div>
  );

  if (showActions) {
    return fields;
  }

  return (
    <Card>
      <CardContent className="space-y-5 pt-6">{fields}</CardContent>
    </Card>
  );
};
